import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { getPublicBusiness, mediaUrl } from "@/api";
import { BrandScope } from "@/theme";
import { Loading } from "./Layout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, ArrowLeft, PlusCircle, RotateCcw, Copy, Check } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}

export const JoinScreen = () => {
  const { slug } = useParams();
  const { joinCustomer, restoreCustomer, activateBusinessCard } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Only intentional logout redirects carry ?loggedout=1 → show the create/restore choice.
  // A fresh public-QR scan (no flag) goes straight to the name-entry create screen.
  const loggedOut = searchParams.get("loggedout") === "1";
  const [business, setBusiness] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [resolving, setResolving] = useState(true); // deciding remembered-card vs onboarding
  const [view, setView] = useState(loggedOut ? "choice" : "create"); // choice | create | restore
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [newCode, setNewCode] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setResolving(true);
    (async () => {
      let b;
      try { b = await getPublicBusiness(slug); }
      catch (e) { if (alive) { setNotFound(true); setResolving(false); } return; }
      if (!alive) return;
      setBusiness(b);
      // Remember this business so a logged-out customer returns here (no re-scan).
      try { localStorage.setItem("bs_slug", slug); } catch (e) {}
      // Auto-open a remembered card ONLY for this exact business. Skip on an
      // explicit logout so the user sees the create/restore choice.
      if (!loggedOut) {
        const u = await activateBusinessCard(b);
        if (!alive) return;
        if (u) { navigate("/", { replace: true }); return; }
      }
      if (alive) setResolving(false);
    })();
    return () => { alive = false; };
  }, [slug]);

  const create = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      // New card only: show the returned recovery code once before opening the card.
      const { recovery_code } = await joinCustomer(slug, name.trim());
      if (recovery_code) setNewCode(recovery_code);
      else navigate("/");
    } catch (err) { setError(formatErr(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const restore = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await restoreCustomer(code.trim());
      try {
        localStorage.setItem("bs_slug", slug);
        localStorage.setItem(`bs_recovery:${slug}`, code.trim());
      } catch (e2) {}
      navigate("/");
    } catch (err) {
      if (err.response?.status === 429) setError(formatErr(err.response?.data?.detail));
      else setError("Dieser Recovery-Code wurde nicht gefunden. Bitte prüfe deine Eingabe.");
    }
    finally { setBusy(false); }
  };

  const goto = (v) => { setError(""); setView(v); };
  const copyNewCode = async () => {
    try { await navigator.clipboard.writeText(newCode); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { /* clipboard unavailable */ }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div><p className="font-display font-extrabold text-xl text-slate-900">Betrieb nicht gefunden</p><p className="text-slate-500 text-sm mt-1">Dieser QR-Code ist ungültig.</p></div>
    </div>
  );
  if (!business || resolving) return <Loading />;

  if (!business.active) return (
    <BrandScope primary={business.primary_color} secondary={business.secondary_color} className="App">
      <div className="max-w-md mx-auto min-h-screen bg-slate-50 sm:border-x border-slate-200 shadow-xl flex items-center justify-center p-6">
        <div data-testid="join-unavailable" className="text-center">
          <div className="mx-auto h-16 w-16 rounded-3xl bg-white ring-1 ring-slate-100 shadow-sm flex items-center justify-center overflow-hidden mb-3">
            {mediaUrl(business.logo_url) ? <img src={mediaUrl(business.logo_url)} alt="" className="h-14 w-14 object-contain" /> : <span className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-primary)" }}>{business.name[0]}</span>}
          </div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900">{business.name}</h1>
          <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed">Das Treueprogramm ist derzeit nicht verfügbar. Bitte versuche es später erneut.</p>
        </div>
      </div>
    </BrandScope>
  );

  const logo = mediaUrl(business.logo_url);

  const Header = ({ title, subtitle }) => (
    <div className="text-center mb-6">
      <div className="mx-auto h-16 w-16 rounded-3xl bg-white ring-1 ring-slate-100 shadow-sm flex items-center justify-center overflow-hidden mb-3">
        {logo ? <img src={logo} alt={business.name} className="h-14 w-14 object-contain" /> : <span className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-primary)" }}>{business.name[0]}</span>}
      </div>
      <h1 className="font-display font-extrabold text-2xl text-slate-900">{title}</h1>
      {subtitle && <p className="text-slate-400 text-sm font-medium mt-1">{subtitle}</p>}
    </div>
  );

  return (
    <BrandScope primary={business.primary_color} secondary={business.secondary_color} className={newCode ? "App bs-shell-host" : "App"}>
      <div className={newCode ? "bs-app-shell" : "contents"}>
      <div className={`max-w-md mx-auto ${newCode ? "bs-app-col flex flex-col" : "min-h-screen"} bg-slate-50 sm:border-x border-slate-200 shadow-xl`}>
        {newCode ? (
          <div data-testid="recovery-onboarding-screen" className="flex-1 flex flex-col justify-center px-5 py-6 [@media(max-height:700px)]:py-3 animate-float-up">
            <Header title="Fast geschafft" />
            <p className="text-center text-[15px] text-slate-600 leading-relaxed -mt-3 mb-6 [@media(max-height:700px)]:mb-4 px-2">
              Sichere deine Treuekarte kurz ab.<br />Mach am besten einen Screenshot von deinem Recovery Code.
            </p>
            <div data-testid="recovery-code-card" className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft overflow-hidden">
              <div className="brand-wave px-5 py-3 flex items-center justify-between gap-3">
                <span className="text-white font-display font-extrabold text-[15px] truncate">{business.name}</span>
                <span className="text-white/85 text-[10px] font-bold uppercase tracking-[0.16em] shrink-0">Bonstelo Treuekarte</span>
              </div>
              <div className="px-5 pt-5 pb-6 text-center">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-[0.14em]">Dein Recovery Code</p>
                <p data-testid="recovery-code-value" className="mt-2 font-display font-extrabold text-[34px] leading-none tracking-[0.12em] tabular-nums whitespace-nowrap select-all" style={{ color: "var(--brand-primary)" }}>{newCode}</p>
                <button type="button" data-testid="recovery-copy-button" onClick={copyNewCode}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold ring-1 ring-slate-200 text-slate-600 hover:bg-slate-50 press transition-colors duration-150">
                  {copied ? <><Check className="h-4 w-4 text-emerald-600" /> Code kopiert</> : <><Copy className="h-4 w-4" /> Code kopieren</>}
                </button>
              </div>
            </div>
            <p className="text-center text-[13px] text-slate-500 leading-relaxed mt-5 [@media(max-height:700px)]:mt-3 px-3">
              Du brauchst ihn, wenn du dein Handy wechselst, deine Browserdaten gelöscht werden oder du deine Treuekarte auf einem anderen Gerät wiederherstellen möchtest.
            </p>
            <Button data-testid="recovery-continue-button" onClick={() => navigate("/")} className="w-full h-14 mt-6 [@media(max-height:700px)]:mt-4 rounded-2xl font-display font-bold text-base shadow-brand press text-white" style={{ background: "var(--brand-primary)" }}>
              Weiter zu meiner Treuekarte
            </Button>
            <p className="text-center text-[11.5px] text-slate-400 leading-relaxed mt-4 px-4">
              Bewahre den Code sicher auf. Wer ihn kennt, kann deine Treuekarte wiederherstellen.
            </p>
          </div>
        ) : view === "create" ? (
          <div className="p-5 pt-8 pb-24 animate-float-up">
            <Header title={business.name} subtitle="Deine digitale Treuekarte" />
            <form onSubmit={create} className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-5 space-y-4">
              <div className="space-y-2">
                <label className="block font-display font-extrabold text-xl text-slate-900 text-center">Wie dürfen wir dich nennen?</label>
                <Input data-testid="join-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name oder Anzeigename" className="h-12 rounded-xl mt-1 text-center" required autoFocus />
              </div>
              {error && <p data-testid="join-error" className="text-sm font-semibold text-rose-600 bg-rose-50 rounded-xl px-3 py-2">{error}</p>}
              <Button data-testid="join-create-button" type="submit" disabled={busy} className="w-full h-14 rounded-2xl font-display font-bold text-base shadow-brand press disabled:opacity-60 text-white" style={{ background: "var(--brand-primary)" }}>
                {busy ? <><Loader2 className="mr-1 h-5 w-5 animate-spin" /> Bitte warten…</> : "Treuekarte erstellen"}
              </Button>
              {loggedOut ? (
                <button type="button" data-testid="join-back-choice" onClick={() => goto("choice")} className="w-full flex items-center justify-center gap-1 text-xs text-slate-400 font-medium"><ArrowLeft className="h-3.5 w-3.5" /> Zurück</button>
              ) : (
                <button type="button" data-testid="join-show-restore" onClick={() => goto("restore")} className="w-full text-xs text-slate-400 font-medium underline underline-offset-2">Du hast schon eine Karte? Wiederherstellen</button>
              )}
            </form>
          </div>
        ) : view === "restore" ? (
          <div className="p-5 pt-8 pb-24 animate-float-up">
            <Header title="Treuekarte wiederherstellen" subtitle={`bei ${business.name}`} />
            <form onSubmit={restore} className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-5 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500">Recovery-Code</label>
                <Input data-testid="restore-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Recovery-Code" className="h-12 rounded-xl uppercase tracking-widest font-display font-bold" required autoFocus />
              </div>
              {error && <p data-testid="restore-error" className="text-sm font-semibold text-rose-600 bg-rose-50 rounded-xl px-3 py-2">{error}</p>}
              <Button data-testid="restore-submit-button" type="submit" disabled={busy} className="w-full h-14 rounded-2xl font-display font-bold text-base shadow-brand press disabled:opacity-60 text-white" style={{ background: "var(--brand-primary)" }}>
                {busy ? <><Loader2 className="mr-1 h-5 w-5 animate-spin" /> Bitte warten…</> : "Karte wiederherstellen"}
              </Button>
              <button type="button" data-testid="restore-back-choice" onClick={() => goto(loggedOut ? "choice" : "create")} className="w-full flex items-center justify-center gap-1 text-sm text-slate-500 font-medium"><ArrowLeft className="h-4 w-4" /> Zurück</button>
            </form>
          </div>
        ) : (
          <div data-testid="join-choice-screen" className="p-5 pt-10 pb-24 animate-float-up">
            <Header title={`Willkommen bei ${business.name}`} subtitle={`${business.reward_title} — deine digitale Treuekarte.`} />
            <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-5 space-y-3">
              <Button data-testid="choice-create-button" onClick={() => goto("create")} className="w-full h-16 rounded-2xl font-display font-bold text-base shadow-brand press text-white flex items-center justify-center gap-2" style={{ background: "var(--brand-primary)" }}>
                <PlusCircle className="h-5 w-5" /> Neue Treuekarte erstellen
              </Button>
              <Button data-testid="choice-restore-button" onClick={() => goto("restore")} variant="outline" className="w-full h-16 rounded-2xl font-display font-bold text-base press flex items-center justify-center gap-2" style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}>
                <RotateCcw className="h-5 w-5" /> Treuekarte wiederherstellen
              </Button>
            </div>
          </div>
        )}
      </div>
      </div>
    </BrandScope>
  );
};
